import { useTranslation } from 'react-i18next';
import { GridViewIcon, ListViewIcon, SearchIcon, UploadCloudIcon } from '@/utils/icons';
import type { DocumentsView } from '../hooks/useDocumentsView';
import styles from './DocumentsBar.module.css';

interface IDocumentsBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  /** Absent when the user may not upload — the button is not rendered. */
  onUploadClick?: () => void;
  view: DocumentsView;
  onViewChange: (view: DocumentsView) => void;
}

const VIEWS: { id: DocumentsView; Icon: (props: { size?: number }) => React.ReactElement }[] = [
  { id: 'list', Icon: ListViewIcon },
  { id: 'grid', Icon: GridViewIcon },
];

export function DocumentsBar({ searchQuery, onSearchChange, onUploadClick, view, onViewChange }: IDocumentsBarProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.bar}>
      <div className={styles.searchWrap} data-tour="documents-search">
        <span className={styles.searchIcons}>
          <SearchIcon size={15} />
        </span>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('documents.searchPlaceholder')}
          aria-label={t('documents.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <div className={styles.viewSwitch} role="group" aria-label={t('documents.view.label')} data-tour="documents-views">
        {VIEWS.map(({ id, Icon }) => (
          <button
            key={id}
            type="button"
            className={`${styles.viewBtn} ${view === id ? styles.viewBtnActive : ''}`}
            aria-pressed={view === id}
            aria-label={t(`documents.view.${id}`)}
            title={t(`documents.view.${id}`)}
            onClick={() => onViewChange(id)}
          >
            <Icon size={16} />
          </button>
        ))}
      </div>
      {onUploadClick && (
        <button type="button" className={styles.uploadBtn} aria-label={t('documents.uploadBtn')} onClick={onUploadClick} data-tour="documents-upload">
          <UploadCloudIcon size={16} />
          {t('documents.uploadBtn')}
        </button>
      )}
    </div>
  );
}
